import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import {
	type CrewDefinitionManagerSlotFormSchema,
	crewDefinitionManagerSlotSchema,
} from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { CrewDefinitionManagerSlotForm } from "@/forms/crew-definition-manager-slot-form";
import { client } from "@/hono";
import { useCrewDefinitions } from "@/hooks/use-crew-definitions";

/** Props Interface */
interface AddManagerSlotModalProps extends ModalProps {
	crewDefinitionId: string;
}

/**
 * The window a manager is given over a crew: the span inside the crew's own span during which they
 * may schedule it. Slots are per manager and may overlap, so a crew can be handed on mid-project
 * without either manager losing the days they already planned.
 */
export function AddManagerSlotModal({
	isOpen,
	onClose,
	crewDefinitionId,
}: AddManagerSlotModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();

	/** Query */
	const { crewDefinitions } = useCrewDefinitions();

	/** Functions */
	function getCrewDefinition() {
		for (const crewDefinition of crewDefinitions) {
			if (crewDefinition.id === crewDefinitionId) return crewDefinition;
		}
		return null;
	}

	/** Computed */
	const crewDefinition = getCrewDefinition();

	/** Hooks */
	// The span opens on the crew's own dates: a window over the whole crew is the common grant, and
	// narrowing it is the deliberate act.
	const methods = useForm<CrewDefinitionManagerSlotFormSchema>({
		resolver: resolver(crewDefinitionManagerSlotSchema),
		values: {
			userId: "",
			startDate: crewDefinition?.startDate ?? "",
			endDate: crewDefinition?.endDate ?? null,
		},
	});

	/** Functions */
	async function handleOnSubmit(json: CrewDefinitionManagerSlotFormSchema) {
		const response = await client.crew_definitions[":crewDefinitionId"]["manager-slots"].$post({
			param: { crewDefinitionId },
			json,
		});
		await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["crew-definitions"]) });
		onClose();
		methods.reset();
	}

	/** Guard */
	if (!crewDefinition) return null;

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading}
				title={t("planning.crews.add_manager_slot")}
				width="500px"
				data-cy="add-manager-slot-modal"
			>
				<Modal.Body>
					<CrewDefinitionManagerSlotForm
						crewStartDate={crewDefinition.startDate}
						crewEndDate={crewDefinition.endDate}
					/>
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
